<template>
  <div>
    <div class="structured-top">
      <h1 v-mark-word>{{ $t('purchase.history.title') }}</h1>
      <span class="lead">{{ $t('purchase.history.hint') }}</span>
    </div>
    <LoadMore
      name="purchases"
      :load="getPurchaseHistory"
      :key="$i18n.locale"
      @load="setHistory"
      class="structured-content positive"
    >
      <Messages id="purchaseHistory" />
      <div class="alert alert-default mb-7" role="alert" v-if="isEmpty">{{ $t('purchase.history.emptyList') }}</div>
      <table class="table structure-table structure-table-borders" v-else>
        <thead>
          <tr>
            <th class="d-none d-xl-table-cell">{{ $t('purchase.history.labels.date') }}</th>
            <th class="d-none d-xl-table-cell">{{ $t('purchase.history.labels.product') }}</th>
            <th class="d-none d-xl-table-cell">{{ $t('purchase.history.labels.amount') }}</th>
            <th class="btn-col">&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          <tr :class="{ 'row-bought': index === justBoughtProductIndex }" v-for="(item, index) in items" :key="index">
            <td>
              <span class="td-header d-block d-xl-none">
                {{ $t('purchase.history.labels.date') }}
              </span>
              {{ $date(item.date) }}
              <a
                class="btn btn-default float-end position-absolute mt-3 me-3 d-xl-none"
                v-bind="linkPropsFor(item)"
                @click="forward"
                v-if="item.url"
              >
                {{ $t('purchase.history.labels.view') }}
              </a>
            </td>
            <td>
              <span class="td-header d-block d-xl-none">
                {{ $t('purchase.history.labels.product') }}
              </span>
              <a v-bind="linkPropsFor(item)" @click="forward">
                {{ item.title }}
              </a>
            </td>
            <td class="mobile-border">
              <span class="td-header d-block d-xl-none">
                {{ $t('purchase.history.labels.amount') }}
              </span>
              {{ $price(item.price) }}
            </td>
            <td class="btn-col d-none d-xl-table-cell action-buttons">
              <a class="btn btn-default float-end" v-bind="linkPropsFor(item)" @click="forward" v-if="item.url">
                {{ $t('purchase.history.labels.view') }}
              </a>
              <OrderWithdrawal v-if="isSubscription(item)" :item="item" class="float-end" @agree="onWithdraw" />
            </td>
          </tr>
        </tbody>
      </table>
    </LoadMore>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import LoadMore from '../components/LoadMore';
import Messages from '../components/Messages';
import OrderWithdrawal from '../components/OrderWithdrawal';

export default {
  components: {
    LoadMore,
    Messages,
    OrderWithdrawal,
  },
  data: () => ({
    items: [],
    highlightProduct: '',
  }),
  computed: {
    ...mapGetters('app', ['isLoading']),
    ...mapState('session', ['profile']),

    isEmpty() {
      return !this.isLoading && this.items.length === 0;
    },
    justBoughtProductIndex() {
      if (!this.highlightProduct) {
        return -1;
      }

      return this.items.findIndex((item) => item.productCode === this.highlightProduct);
    },
  },
  methods: {
    ...mapActions('purchase', ['getPurchaseHistory', 'clearCart']),
    ...mapActions('alerts', ['showMessage']),
    ...mapMutations('session', ['redirectAfterLogin']),

    redirectIfNotLoggedIn() {
      if (!this.profile.personId) {
        this.redirectAfterLogin(this.$route.fullPath);
        this.$router.push({ name: `login-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } });
      }
    },

    setHistory({ items }) {
      this.items = items;
      this.showJustBoughtMessage();
    },

    onWithdraw({ item }) {
      item.withdrawnAt = new Date().toISOString();
    },

    linkPropsFor(item) {
      if (item.url) {
        return {
          href: item.url,
          target: '_blank',
        };
      }

      if(item.product) {
        return { href: this.$router.resolve({ name: `report-${this.$i18n.locale}`, params: { ...item.product, lang: this.$i18n.locale } }).href, target: '_blank' };
      }
    },

    isSubscription(item) {
      return item.type === 'subscription';
    },

    forward(event) {
      const link = event.target;

      if (!link.target) {
        event.preventDefault();
        this.$router.push({ path: link.getAttribute('href') });
      }
    },

    showJustBoughtMessage() {
      if (this.highlightProduct) {
        let msgText = this.$t('purchase.success');

        if (this.justBoughtProductIndex !== -1) {
          const { product } = this.items[this.justBoughtProductIndex];

          if (product.promotion === 'todayIsFree' || product.price === 0) {
            msgText = this.$t('purchase.history.freeReportMsg');
          } else if (this.highlightProduct === 'MYPDS') {
            msgText = this.$t('purchase.successMYPDS');
          }
        }
        this.showMessage({ target: 'purchaseHistory', type: 'success', text: msgText });
        this.$router.replace({ path: this.$route.path });
      }
    },
  },
  created() {
    this.redirectIfNotLoggedIn();
    this.highlightProduct = this.$route.query.product;

    if (this.highlightProduct) {
      this.clearCart();
    }
  },
  metaInfo() {
    return {
      title: this.$t('purchase.meta.title'),
      meta: [
        { vmid: 'keywords', name: 'keywords', content: this.$t('purchase.meta.keywords') },
        { vmid: 'description', name: 'description', content: this.$t('purchase.meta.description') },
      ],
    };
  },
};
</script>
